import React, { useMemo, useState, useRef, useEffect } from 'react'
import moment from 'moment'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'

import { useAppDispatch } from '@/redux/store'
import NotificationIcon from '@/modules/base/components/notification/overlay/notification-icon'
import { EMAIL_CONFIRMATION_NOTIFICATION_TYPE, INotification } from '@/modules/base/components/notification/notificationService'
import { markAsRead } from 'redux/ducks/newsfeed'
import { toggleNotificationOverlay } from '@/redux/ducks/menu/main'
import { useNavigate } from 'react-router'
import lang from '@/lang'
import ButtonSquare, { VARIANTS as ButtonSquareVariants } from '@/modules/base/components/buttons/square/button-square'
import openConfirmEmailModal from '@/modules/_helpers/openConfirmEmailModal'

type StyleProps = { clickable: boolean; expanded: boolean }

const useStyles = makeStyles((theme) => ({
  notification: {
    minHeight: 92,
    padding: '14px 30px',
    backgroundColor: 'none',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    color: theme.mode.color.default,
  },
  notificationContent: ({ clickable }: StyleProps) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    cursor: clickable ? 'pointer' : 'default',
    '&:hover': {
      color: clickable ? theme.primary : theme.mode.color.default,
    },
  }),
  notificationBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    flex: '1 1 auto',
    minWidth: 0,
    marginLeft: 20,
    marginRight: 10,
  },
  notificationTitle: {
    fontWeight: theme.fontWeights.semiBold,
    marginBottom: 4,
  },
  notificationText: ({ expanded }: StyleProps) => ({
    display: '-webkit-box',
    overflow: 'hidden',
    wordWrap: 'break-word',
    textOverflow: 'ellipsis',
    '-webkit-line-clamp': expanded ? 'unset' : 3, // number of lines to show
    '-webkit-box-orient': 'vertical',
  }),
  notificationDate: {
    color: theme.mode.color.medium,
    whiteSpace: 'nowrap',
    textAlign: 'right',
    marginLeft: 'auto',
    alignSelf: 'flex-end',
  },
  ctaButton: {
    marginTop: 10,
  },
  unreadBadge: {
    height: 10,
    width: 10,
    display: 'block',
    borderRadius: '50%',
    color: theme.white,
    backgroundColor: theme.error,
    fontSize: 10,
    textAlign: 'center',
    lineHeight: '23px',
    position: 'absolute',
    right: 30,
    top: 20,
  },
  animated: {
    animationName: '$pulse',
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
  },
  showMoreButton: {
    display: 'flex',
    alignSelf: 'flex-end',
    marginLeft: 45,
    marginTop: 8,
    marginBottom: 5,
    fontSize: 14,
    color: theme.primary,
    cursor: 'pointer',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgb(231, 60, 91, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(231, 60, 91, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(231, 60, 91, 0)',
    },
  },
}
))

interface INotificationProps extends INotification {
  dateFormat: string
}

const Notification = ({ id, readDate, createdAt, icon, link, text, title, metaData, dateFormat = 'DD.MM.YYYY, HH:mm' }: INotificationProps) => {
  const dispatch = useAppDispatch()
  const unread = !readDate
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef<HTMLDivElement>(null)
  // DO-13065: the "verify your e-mail" item has no link — its whole row and the button open the DOI modal
  const isEmailConfirmation = metaData?.type === EMAIL_CONFIRMATION_NOTIFICATION_TYPE
  // The API forwards the push `title` of every template since DO-13065, but the list never showed one.
  // Only the CTA item renders it as a headline — showing it everywhere is a separate design decision.
  const showTitle = !!title && isEmailConfirmation
  const classes = useStyles({ clickable: !!link || isEmailConfirmation, expanded })

  useEffect(() => {
    // Check if text is truncated
    const element = textRef.current
    if (element) {
      // +1 for rounding errors to handle sub-pixel rendering
      const isClamped = element.scrollHeight > element.clientHeight + 1
      setIsTruncated(isClamped)
    }
  }, [text])

  const onVerifyEmail = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    // deliberately NOT marked as read here — the backend does that once the address is confirmed,
    // so a user who presses "Abbrechen" keeps the unread reminder
    dispatch(toggleNotificationOverlay(false))
    // soft reminder: the modal offers "Abbrechen", never "Abmelden" (that stays with the day-8 gate)
    openConfirmEmailModal()
  }

  const onClick = () => {
    if (isEmailConfirmation) { return onVerifyEmail() }
    if (unread) dispatch(markAsRead(id))
    if (!link) { return }
    navigate(link)
    dispatch(toggleNotificationOverlay(false))
  }

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setExpanded(!expanded)
  }

  const unreadBadge = useMemo(() => {
    if (!unread) { return null }
    return <span className={clsx(classes.unreadBadge, classes.animated)}></span>
  }, [unread])

  return (
    <div className={classes.notification}>
      <div className={classes.notificationContent} onClick={onClick}>
        <NotificationIcon icon={icon} width='25px' height='25px' />
        <div className={classes.notificationBody}>
          {showTitle && <div className={classes.notificationTitle} data-qs-id='notification-title'>{title}</div>}
          <div ref={textRef} className={classes.notificationText} dangerouslySetInnerHTML={{ __html: text }} />
          {isEmailConfirmation && (
            <ButtonSquare
              variant={ButtonSquareVariants.small}
              className={classes.ctaButton}
              label={lang.confirmEmail.verifyNow}
              onClick={onVerifyEmail}
              dataQsId='notification-verify-email-button'
            />
          )}
        </div>
        <span className={classes.notificationDate}>{moment(createdAt, 'YYYY-MM-DDTHH:mm:ss.SSSZ').format(dateFormat)}</span>
        {unreadBadge}
      </div>
      {(isTruncated || expanded) && (
        <span className={classes.showMoreButton} onClick={toggleExpanded}>
          {expanded ? lang.notifications.showLess : lang.notifications.showMore}
        </span>
      )}
    </div>
  )
}

export default Notification
